/*
 * Which header a route gets.
 *
 * ── Why one component decides ──────────────────────────────────────────────
 *
 * HeaderTest is mounted outside <Routes> so it renders everywhere, which is
 * what makes it global chrome and also what leaves an individual page no way
 * to opt out. The redesign introduces a second, lighter header, and the two
 * must never both appear. Putting the choice here means it is made once, in a
 * place a reviewer can read in ten seconds, rather than page by page.
 *
 * An early `return null` inside HeaderTest would not have worked: it has four
 * useEffects and a dozen useStates, so a rules-of-hooks-legal early return
 * sits below all of them — React still mounts it and still runs every effect,
 * including one that calls checkByemail(String(user)) with the literal
 * "null" when signed out. Rendering nothing is not the same as not mounting.
 *
 * App() renders <BrowserRouter>, so App() itself cannot call useLocation().
 * A child can. ScrollToTop next door is the same shape for the same reason.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import HeaderTest from "../components/Header_test";
import LandingHeader from "../components/Landing/LandingHeader";

/** Exact paths on the new design. */
const REDESIGNED = new Set<string>([
  "/",
  "/about",
  "/how-master-login-works",
]);

/*
 * /indexx-exchange/about is deliberately NOT here.
 *
 * It is the Exchange's own about page, still linked from the old footer on 56
 * pages and from the megamenu, and it keeps the old header so it matches the
 * pages that link to it. The estate's about page is /about.
 */

/**
 * The whole sign-in flow: login, password, signup and its three steps, and
 * reset-password. A prefix, because those routes are added and renamed often
 * and a list would rot silently.
 */
const REDESIGNED_PREFIXES = ["/auth/"];

/**
 * …except the OIDC callback. It is on screen for a few hundred milliseconds
 * mid-redirect and renders its own full-bleed "Signing you in…" panel;
 * flashing a navigation bar onto it would be noise, not consistency.
 */
/*
 * …and /homepage1, which ships its own floating navigation bar inside the page
 * (Homepage1/Navbar). Two bars would stack. Both spellings are here because
 * App.tsx registers the route twice, "/hompage1" included, and a visitor who
 * follows the typo'd link should still get the page it was built with.
 */
const NO_CHROME = new Set<string>([
  "/auth/callback",
  "/homepage1",
  "/hompage1",
]);

/** On the redesign at all — including the callback, which has no bar. */
export function isRedesigned(pathname: string): boolean {
  if (NO_CHROME.has(pathname)) return true;
  if (REDESIGNED.has(pathname)) return true;
  return REDESIGNED_PREFIXES.some((p) => pathname.startsWith(p));
}

/** On the redesign AND gets the new bar. */
export function usesNewChrome(pathname: string): boolean {
  return isRedesigned(pathname) && !NO_CHROME.has(pathname);
}

/**
 * The dark page surface, for every redesigned route.
 *
 * ── Why the body, and why here ─────────────────────────────────────────────
 *
 * <DarkMode/> is what sets the app's theme on a cold visit, and it is mounted
 * inside HeaderTest — which these routes no longer render. So the body keeps
 * its light default while the page drawn on top of it is dark, and the
 * difference shows wherever the page does not paint: as a light band between
 * the navigation bar and the form on every /auth route, and in the overscroll
 * gutters everywhere.
 *
 * It lived as a copy-pasted effect inside Landing and About, which is why
 * only those two were right. It belongs next to the decision it depends on —
 * the same predicate that picks the header now also picks the surface, so a
 * route added to REDESIGNED cannot get one without the other.
 *
 * Restored on the way out, so the rest of the app keeps whatever theme
 * DarkMode gave it.
 */
function useDarkBody(dark: boolean) {
  useEffect(() => {
    if (!dark) return;
    const previous = document.body.style.background;
    document.body.style.background = "#0b0b0d";
    return () => {
      document.body.style.background = previous;
    };
  }, [dark]);
}

export default function SiteChrome() {
  const { pathname } = useLocation();

  /* Before the early returns: a hook cannot sit below a conditional return,
     and the callback route returns null while still wanting the dark body. */
  useDarkBody(isRedesigned(pathname));

  if (NO_CHROME.has(pathname)) return null;
  if (usesNewChrome(pathname)) return <LandingHeader />;
  return <HeaderTest />;
}
